<script setup lang="ts">
import { IRadioButtons } from '@inkline/inkline'
import FilterPopoverBase from '../modules/FilterPopoverBase.vue'
import { useFilterPopoverState } from '~/composables/filter/useFilterPopoverState'
import type { useFilterState } from '~/composables/filter/useFilterState'
import type {
  EstateListingEssentialResponse,
  EstateListingResponse,
} from '~/types/api/EstateListing'

const props = defineProps<{
  officesResponse: EstateListingResponse | EstateListingEssentialResponse
  filterState: ReturnType<typeof useFilterState>
}>()
const { t, te } = useI18n()

const filterPopoverState = useFilterPopoverState()

function getOptionByType(
  type:
    | 'A_Class'
    | 'Blank'
    | 'Brownfield'
    | 'City_Logistics'
    | 'Land'
    | 'Object'
) {
  return {
    id: type,
    label: te(`filter.subType.${type}`) ? t(`filter.subType.${type}`) : type,
    count: computed(() => props.officesResponse.filter.sub_type?.[type] || 0),
    disabled: computed(
      () =>
        selected.value !== type &&
        !props.officesResponse.filter.sub_type?.[type]
    ),
  }
}

const options = ref([
  getOptionByType('A_Class'),
  // getOptionByType('Blank'),
  getOptionByType('Brownfield'),
  getOptionByType('City_Logistics'),
  getOptionByType('Land'),
  getOptionByType('Object'),
])

// TODO
const initialValue = props.filterState.filterValues.value.sub_type
const selected = ref(initialValue) as Ref<string | undefined>

const triggerValue = computed(() => {
  if (selected.value) {
    return t(`filter.subType.${selected.value}`)
  }

  return t('filter.subType.all')
})

watch(
  () => selected.value,
  (value) => {
    if (!value) {
      props.filterState.actions.deleteValue('sub_type')
    } else {
      props.filterState.actions.updateValue('sub_type', value)
    }

    props.filterState.actions.submitFilter()
  }
)

const isFiltered = computed(() => {
  return (
    props.filterState.filterValues.value.availability_now ||
    props.filterState.filterValues.value.availability_to
  )
})

function onToggleChange(value: string) {
  if (selected.value === value) {
    selected.value = undefined

    return
  }

  selected.value = value
}

watch(
  () => props.filterState.filterValues.value.sub_type,
  (newValue) => {
    if (newValue === selected.value) return

    selected.value = newValue
  }
)
</script>

<template>
  <FilterPopoverBase
    :filter-popover-state="filterPopoverState"
    :show-reset-button="isFiltered"
  >
    <template #trigger-label>{{ $t('filter.subType.title') }}</template>

    <template #trigger-value>{{ triggerValue }}</template>

    <template #popover-body>
      <div class="c-filter-offer-type">
        <div class="c-filter-offer-type__title">
          {{ $t('filter.subType.title') }}
        </div>

        <div class="c-filter-offer-type__toggle">
          <IRadioButtons
            :model-value="selected"
            :options="options"
            size="sm"
            color="primary"
            @update:model-value="onToggleChange"
          >
            <template #option="{ option }">
              <span>{{ option.label }}</span>
              <span class="sc-count">({{ option.count }})</span>
            </template>
          </IRadioButtons>
        </div>
      </div>
    </template>
  </FilterPopoverBase>
</template>

<style lang="scss" scoped>
@use '@inkline/inkline/css/mixins' as *;

.c-filter-offer-type {
  display: flex;
  flex-direction: column;
  gap: 10px;

  &__toggle {
    :deep(.checkable-button-group) {
      --checkable-button-group--button--background: var(
        --color-green-primary-light-2
      );

      padding: 0;
      border: none;
      box-shadow: none;
      margin: 0;
      display: flex;
      flex-direction: column;
      align-items: stretch;
      gap: 4px;

      .button {
        justify-content: stretch;
        margin: 0;

        .button-content {
          width: 100%;
          justify-content: space-between;
          gap: 4px;
        }

        .sc-count {
          font-weight: 400;
          opacity: 0.5;
          min-width: 30px;
          text-align: right;
        }

        &:not(.disabled) {
          &:hover,
          &:focus {
            --checkable-button-group--button--background: var(--color-gray-200);
          }
        }
      }
    }
  }

  &__actions {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
}
</style>
